import axios from 'axios';
import { api, getApiErrorCode } from '@/api/client';

export type SmartMarketplaceRunStatus = 'queued' | 'running' | 'completed' | 'failed';

export type SmartMarketplaceFinding = {
  summary: string;
  evidence_refs?: string[];
  classification?: string | null;
};

export type AnalysisTable = {
  title: string;
  columns: string[];
  rows: string[][];
  note?: string | null;
};

export type SmartMarketplaceCandidate = {
  recommendation_id: string;
  entity_scope: string;
  action_type: string;
  current_value?: string | null;
  proposed_value?: string | null;
  rationale: string;
  confidence: string;
  risk_level: string;
  assumptions?: string[];
};

export type SpecialistOutput = {
  agent_name: string;
  status: string;
  findings?: SmartMarketplaceFinding[];
  tables?: AnalysisTable[];
  candidates?: SmartMarketplaceCandidate[];
  assumptions?: string[];
  missing_data?: string[];
  confidence?: string;
  risks?: string[];
  warnings?: string[];
  error?: string | null;
};

export type SmartMarketplaceRecommendation = {
  recommendation_id: string;
  entity_scope: string;
  action_type: string;
  current_value?: string | null;
  proposed_value?: string | null;
  rationale: string;
  confidence: string;
  risk_level: string;
  guardrail_status?: string;
  guardrail_reasons?: string[];
  expected_impact?: string | null;
  dependencies?: string[];
  measurement_window?: string | null;
  assumptions?: string[];
  approval?: string;
  execution_eligibility?: string;
  status: string;
  rank?: number | null;
  agent_name?: string | null;
};

export type SmartMarketplaceConflict = {
  conflict_id: string;
  entity_scope: string;
  recommendation_ids: string[];
  description: string;
  resolution: string;
  resolution_reason?: string | null;
};

export type AgentRunSummary = {
  agent_name: string;
  status: string;
  duration_ms: number;
  retry_count?: number;
  candidate_count?: number;
  error?: string | null;
};

export type SmartMarketplaceTask =
  | 'optimize'
  | 'brands'
  | 'keyword'
  | 'search_term'
  | 'placement'
  | 'asin'
  | 'targeting';

export type ToolCallRecord = {
  tool_name: string;
  tool_call_id: string;
  args?: unknown;
  result?: unknown;
};

export type AgentTrace = {
  agent_name: string;
  instructions?: string;
  user_prompt?: string;
  model_name?: string | null;
  usage?: Record<string, unknown> | null;
  duration_ms?: number;
  retry_count?: number;
  error?: string | null;
  output?: Record<string, unknown> | null;
  tool_calls?: ToolCallRecord[];
  messages?: unknown[];
  attempts?: { error?: string | null }[];
};

export type SmartMarketplaceDecision = {
  request_id: string;
  run_status: string;
  summary: string;
  recommendations?: SmartMarketplaceRecommendation[];
  blocked_actions?: SmartMarketplaceRecommendation[];
  conflicts?: SmartMarketplaceConflict[];
  missing_data?: string[];
  questions?: string[];
  agent_run_summary?: AgentRunSummary[];
  decision_rationale: string;
  rationale_points?: string[];
  warnings?: string[];
};

export type SmartMarketplaceRead = {
  key: string;
  error?: string | null;
};

export type SmartMarketplaceRun = {
  id: string;
  request_id: string;
  profile_id: string;
  brand_id: string;
  marketplace: string;
  task_type: string;
  execution_mode: string;
  model: string;
  environment?: string;
  status: SmartMarketplaceRunStatus | string;
  run_status: string | null;
  decision_json: SmartMarketplaceDecision | null;
  error: string | null;
  created_at: string | null;
  started_at: string | null;
  finished_at: string | null;
  agents_json?: SpecialistOutput[] | null;
  reads_json?: SmartMarketplaceRead[] | null;
  traces_json?: AgentTrace[] | null;
};

export type SmartMarketplaceRunList = {
  runs: SmartMarketplaceRun[];
};

export type ClientSettingsGoal = 'target_acos' | 'target_roas' | 'efficiency';

export type ClientSettings = {
  objective: {
    goal: ClientSettingsGoal;
    target_acos: number | null;
    target_roas: number | null;
  } | null;
  constraints: {
    max_bid_change_pct: number | null;
    max_budget_change_pct: number | null;
    min_placement_multiplier: number | null;
    max_placement_multiplier: number | null;
  } | null;
};

export type ClientSettingsRecord = {
  brand_id: string;
  brand_name: string | null;
  settings: ClientSettings | null;
};

function runsPath(profileId: string, suffix = ''): string {
  return `/api/v1/profiles/${encodeURIComponent(profileId)}/smart-marketplace/runs${suffix}`;
}

export async function startRun(
  profileId: string,
  taskType: SmartMarketplaceTask = 'optimize'
): Promise<SmartMarketplaceRun> {
  const response = await api.post<SmartMarketplaceRun>(runsPath(profileId), {
    task_type: taskType,
    execution_mode: 'recommendation_only',
  });
  return response.data;
}

export async function listRuns(profileId: string): Promise<SmartMarketplaceRunList> {
  const response = await api.get<SmartMarketplaceRunList>(runsPath(profileId));
  return response.data;
}

function clientSettingsPath(profileId: string): string {
  return `/api/v1/profiles/${encodeURIComponent(profileId)}/smart-marketplace/client-settings`;
}

export async function getClientSettings(profileId: string): Promise<ClientSettingsRecord> {
  const response = await api.get<ClientSettingsRecord>(clientSettingsPath(profileId));
  return response.data;
}

export async function saveClientSettings(
  profileId: string,
  settings: ClientSettings
): Promise<ClientSettingsRecord> {
  const response = await api.put<ClientSettingsRecord>(clientSettingsPath(profileId), settings);
  return response.data;
}

export async function getRun(profileId: string, runId: string): Promise<SmartMarketplaceRun> {
  const response = await api.get<SmartMarketplaceRun>(
    runsPath(profileId, `/${encodeURIComponent(runId)}`)
  );
  return response.data;
}

export function isActiveRunStatus(status: string | null | undefined): boolean {
  return status === 'queued' || status === 'running';
}

export function smartMarketplaceErrorMessage(error: unknown, fallback: string): string {
  const code = getApiErrorCode(error);
  if (code === 'brand_not_linked') return 'This profile is not linked to a brand.';
  if (code === 'super_admin_required') return 'Super admin access is required.';
  if (axios.isAxiosError(error)) {
    const detail = error.response?.data?.detail;
    if (typeof detail === 'string' && detail.trim()) return detail.trim();
    if (detail && typeof detail === 'object' && 'message' in detail) {
      const message = (detail as { message?: unknown }).message;
      if (typeof message === 'string' && message.trim()) return message.trim();
    }
  }
  return fallback;
}
